import { createContext, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeAntiforgeryToken, setUnauthorizedHandler, ApiError } from '@/api/client';
import type {
  CurrentUser,
  LoginRequest,
  LoginResponse,
  MfaVerifyRequest,
  MfaVerifyResponse,
} from '@/api/types';

/**
 * Session state for the whole app, derived from `GET /api/auth/me`.
 *
 * There is deliberately no client-side copy of the identity: the cookie is
 * HttpOnly and the server is the only thing that can say who the user is. Roles
 * and permission claims come from the same call, so a guard can never disagree
 * with what the backend would enforce.
 */

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

interface AuthContextValue {
  user: CurrentUser | null;
  /** True until the first /me call settles; guards must wait rather than redirect. */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  /** Second half of an MFA sign-in; completes what `login` started. */
  verifyMfa: (request: MfaVerifyRequest) => Promise<MfaVerifyResponse>;
  logout: () => Promise<void>;
  /** Re-reads /me, e.g. after a password change clears mustChangePassword. */
  refresh: () => Promise<unknown>;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery<CurrentUser | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        // skipUnauthorizedHandler: a 401 here is the expected answer for an
        // anonymous visitor, not a session that just expired. Letting it reach
        // the global handler would fire a redirect on every cold load of the
        // login page.
        return await api.get<CurrentUser>('/api/auth/me', { skipUnauthorizedHandler: true });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    // The session does not change behind the app's back, and every mutation that
    // could affect it invalidates this key explicitly.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // The token is bound to the current identity, so it is minted once at startup
  // and re-minted after every sign-in and sign-out.
  useEffect(() => {
    void primeAntiforgeryToken();
  }, []);

  // A 401 from anywhere means the cookie is gone: drop the cached session so the
  // guards re-evaluate and send the user to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) =>
      api.post<LoginResponse>('/api/auth/login', credentials),
    onSuccess: async (result) => {
      if (!result.succeeded) return;
      await primeAntiforgeryToken();
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });

  // The cookie is issued by this call, not by /api/auth/login, when the account
  // has a second factor. It therefore needs exactly the same follow-up as a
  // password sign-in: the antiforgery token is bound to the identity, so it must
  // be re-primed before the next mutation. Keeping it here rather than in the
  // challenge page keeps that rule in one place.
  const verifyMfaMutation = useMutation({
    mutationFn: (request: MfaVerifyRequest) =>
      api.post<MfaVerifyResponse>('/api/auth/mfa/verify', request),
    onSuccess: async (result) => {
      if (!result.succeeded) return;
      await primeAntiforgeryToken();
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSettled: async () => {
      // Clear regardless of outcome: if logout failed because the cookie had
      // already expired, the user is signed out either way.
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.clear();
      await primeAntiforgeryToken();
    },
  });

  const login = useCallback(
    (credentials: LoginRequest) => loginMutation.mutateAsync(credentials),
    [loginMutation],
  );

  const verifyMfa = useCallback(
    (request: MfaVerifyRequest) => verifyMfaMutation.mutateAsync(request),
    [verifyMfaMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const refresh = useCallback(() => refetch(), [refetch]);

  const value = useMemo<AuthContextValue>(() => {
    const current = user ?? null;
    return {
      user: current,
      isLoading,
      isAuthenticated: current != null,
      login,
      verifyMfa,
      logout,
      refresh,
      hasRole: (role) => current?.roles.some((r) => r.toLowerCase() === role.toLowerCase()) ?? false,
      hasPermission: (permission) =>
        current?.permissions.some((p) => p.toLowerCase() === permission.toLowerCase()) ?? false,
    };
  }, [user, isLoading, login, verifyMfa, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
